import TechnologySymbol from './TechnologySymbol';
import { getTechnologySymbolMeta } from '@/utils/technologySymbols';
import { cn } from '@/lib/utils';

interface TechStackPanelProps {
  technologies?: string[];
  className?: string;
}

function dedupeTechnologies(technologies: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const tech of technologies) {
    const label = tech?.trim();
    if (!label || label.toLowerCase() === 'other') continue;

    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(label);
  }

  return result;
}

const TechStackPanel = ({ technologies = [], className }: TechStackPanelProps) => {
  const stack = dedupeTechnologies(technologies);

  return (
    <section
      className={cn(
        'mb-6 rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-[1.15rem] py-4 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]',
        className,
      )}
      aria-label="Detected technology stack">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 text-[0.6875rem] font-bold uppercase tracking-[0.12em] text-[var(--inv-text-muted)]">
          Tech stack
        </h2>
        {stack.length > 0 ? (
          <span className="text-[0.75rem] tabular-nums text-[var(--inv-text-secondary)]">
            {stack.length} detected
          </span>
        ) : null}
      </div>

      {stack.length === 0 ? (
        <p className="m-0 text-sm text-[var(--inv-text-secondary)]">
          No technologies detected for this repository yet.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {stack.map(tech => {
            const meta = getTechnologySymbolMeta(tech);
            return (
              <li key={tech}>
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--inv-border-soft-2)] bg-[var(--inv-surface-muted)] py-1 pl-1 pr-2.5 text-[0.8125rem] font-medium text-[var(--inv-text-secondary)]"
                  title={meta.label}>
                  <TechnologySymbol technology={tech} size="sm" />
                  <span className="max-w-[10rem] truncate">{meta.label}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default TechStackPanel;
